/**
 * judgement.js - the engine judges the instrumentation-dependent boxes
 *
 * ENGINE-BUILD.md section 3.4 says "the engine does not judge speech" and
 * hands every component to the game. That is right for the 163 boxes whose
 * evidence is the transcript plus the game's own state - only the game knows
 * whether the sequence was logical or the object was the one on the grid.
 *
 * It is wrong for the 28 boxes that depend on instrumentation. Those are not
 * judgements about speech at all; they are judgements about the SHAPE of the
 * trial - which rung was live, whether a hint had fired, whether any sound was
 * made. Leaving those to the game would mean every game reimplementing the
 * same arithmetic over the same record and getting it subtly differently,
 * which is exactly how a chart stops meaning the same thing in every game
 * (section 9.1).
 *
 * So the division is:
 *
 *   the game    produces the trial record (trial.js) and judges the 163
 *               content boxes it alone can see
 *   the engine  judges all 28 instrumentation boxes from that record
 *   nobody      judges the 21 observational boxes - no sensor reaches them,
 *               or, for Phase 10 D, no single utterance can
 *
 *   Phase 1 B         5 boxes   capability: audio_energy
 *   Phase 2 A/B/C    18 boxes   capability: prompt_sequence
 *   Phase 6 D         5 boxes   capability: prompt_sequence
 *                    --
 *                    28
 *
 * ---------------------------------------------------------------------------
 * WHAT IS SPEC, WHAT IS ENGINE
 *
 * The rules below are COMPONENT-JUDGEMENT.md section 5 turned into code. Where
 * that document leaves a boundary open - and it does, in four places - the
 * choice is marked ENGINE DECISION with its reasoning. None of them can be
 * settled by reading GAME-LEVEL-ENGINE.md v1.0, because the spec describes the
 * skills and not how a browser detects them.
 * ========================================================================= */

import {
  getBoxesForLevel,
  getAssessableBoxes,
  getStrictness,
  boxState,
  validateCapabilities,
  CONDITIONALS,
} from './framework.js';

import {
  supportIndexOf,
  qualifyingVocalisations,
  MIN_VOCALISATION_MS,
  validateTrialRecord,
} from './trial.js';

/** The rung at which the target word is simply given to the child. */
const FULL_MODEL_RUNG = 'full_model';

/* =========================================================================
 * TARGET MATCHING
 *
 * "Produced the target" is needed by Phase 2 A/B/C. It is deliberately a
 * small, dumb, documented matcher rather than a clever one.
 *
 * Section 7.4: the section 8.3 "loose" band promises "ap" for apple, and the
 * Web Speech API cannot deliver it - it returns dictionary words, never a
 * partial phoneme string. The honest approximation the document lands on is:
 * accept any of the alternatives, and accept low confidence. That is what this
 * does. The prefix rule below is a small extra allowance for the loose band
 * where the recogniser genuinely does return a shorter real word ("app" for
 * "apple"); it never applies at moderate or above.
 * ========================================================================= */

const NORMALISE_RE = /[^a-z0-9' ]+/g;

/** Lowercase, strip punctuation, collapse whitespace, split. */
export function tokenise(text) {
  if (typeof text !== 'string') return [];
  return text.toLowerCase().replace(NORMALISE_RE, ' ').split(/\s+/).filter(Boolean);
}

function tokenMatches(heard, wanted, rules) {
  if (heard === wanted) return 'exact';
  if (!rules.allowApproximation) return null;
  const min = rules.minPrefixChars || 0;
  if (min <= 0) return null;
  if (heard.length >= min && wanted.startsWith(heard)) return 'prefix';
  if (wanted.length >= min && heard.startsWith(wanted)) return 'prefix';
  return null;
}

/**
 * Is every token of `target` present in `candidate`?
 * Order-free: word order is Phase 5 D's box, not this one's.
 */
function candidateContainsTarget(candidate, targetTokens, rules) {
  const heard = tokenise(candidate);
  let rule = 'exact';
  for (const wanted of targetTokens) {
    const hit = heard.find((h) => tokenMatches(h, wanted, rules));
    if (!hit) return null;
    if (tokenMatches(hit, wanted, rules) === 'prefix') rule = 'prefix';
  }
  return rule;
}

/**
 * Did the child produce the target word or phrase?
 *
 * Section 5, Phase 1 C: "Target word present in transcript or in any
 * alternative above the phase floor."
 *
 * ENGINE DECISION - an alternative whose confidence is null is accepted.
 * Firefox and some Safari builds report no confidence at all; rejecting those
 * alternatives would make the alternatives rule a Chrome-only feature, and
 * scoring.assessRecognition() already treats a null confidence as "unknown,
 * trust it" for exactly this reason (ENGINE-BUILD.md section 2.5).
 *
 * @param {object} input
 * @param {number} input.phase           selects the strictness band
 * @param {string} input.target          the word or phrase expected
 * @param {string|null} [input.transcript]
 * @param {Array<{transcript:string, confidence:number|null}>} [input.alternatives]
 * @returns {{produced:boolean, matchedIn:'transcript'|'alternative'|null,
 *            matched:string|null, rule:'exact'|'prefix'|null, reason:string}}
 */
export function targetProduced({ phase, target, transcript, alternatives = [] }) {
  const strictness = getStrictness(phase);
  const rules = strictness ? strictness.rules : { allowApproximation: false, minPrefixChars: 0 };
  const floor = strictness ? strictness.rules.minConfidence : 0;

  const targetTokens = tokenise(target);
  if (targetTokens.length === 0) {
    return {
      produced: false, matchedIn: null, matched: null, rule: null,
      reason: 'No target was supplied, so nothing could be matched.',
    };
  }

  const direct = transcript ? candidateContainsTarget(transcript, targetTokens, rules) : null;
  if (direct) {
    return {
      produced: true, matchedIn: 'transcript', matched: transcript, rule: direct,
      reason: `Target "${target}" found in the transcript (${direct} match).`,
    };
  }

  for (const alt of alternatives) {
    if (!alt || typeof alt.transcript !== 'string') continue;
    // null = the browser did not score it. Trusted, not discarded.
    if (alt.confidence !== null && alt.confidence !== undefined && alt.confidence < floor) continue;
    const hit = candidateContainsTarget(alt.transcript, targetTokens, rules);
    if (hit) {
      return {
        produced: true, matchedIn: 'alternative', matched: alt.transcript, rule: hit,
        reason: `Target "${target}" found in alternative "${alt.transcript}" (${hit} match).`,
      };
    }
  }

  return {
    produced: false, matchedIn: null, matched: transcript || null, rule: null,
    reason: transcript
      ? `Target "${target}" is not present in "${transcript}" or any alternative.`
      : `Target "${target}" was not produced - nothing was recognised.`,
  };
}

/* =========================================================================
 * TRIAL SHAPE - the four questions every prompt_sequence box asks
 * ========================================================================= */

/**
 * Reduce a normalised trial record to the facts the judgements need.
 *
 * "Before onset" everywhere means before the child STARTED speaking. A model
 * that played after the child began cannot have prompted them, and a hint that
 * fired after they began did not help - scoring either against the child would
 * punish them for being fast.
 *
 * ENGINE DECISION - when `modelPlayed` is true but `modelEndedAt` is absent,
 * the model is assumed to have played BEFORE onset. That is the conservative
 * direction: it costs the child a box they might have earned, rather than
 * awarding independence that was not demonstrated. Same for `hintFired` /
 * `hintFiredAt`.
 *
 * @param {object} trial normalised trial record
 * @returns {object}
 */
export function trialShape(trial) {
  const onset = trial.spoke ? trial.speechStartedAt : null;

  const modelBeforeOnset = Boolean(trial.modelPlayed) && (
    trial.modelEndedAt === null || onset === null || trial.modelEndedAt <= onset
  );
  const hintBeforeOnset = Boolean(trial.hintFired) && (
    trial.hintFiredAt === null || onset === null || trial.hintFiredAt <= onset
  );

  const nominalIdx = supportIndexOf(trial.nominalRung);
  const onsetIdx = supportIndexOf(trial.supportRung);

  // Any rung at or below full_model that was live before the child spoke.
  const fullModelRungBeforeOnset = (trial.supportSequence || []).some(
    (e) => e.rungId === FULL_MODEL_RUNG && (onset === null || e.at <= onset)
  );

  return {
    spoke: Boolean(trial.spoke),
    respondedToPrompt: Boolean(trial.respondedWithinWindow),
    onsetLatencyMs: trial.onsetLatencyMs,
    nominalRung: trial.nominalRung,
    rungAtOnset: trial.supportRung,
    nominalIndex: nominalIdx,
    onsetIndex: onsetIdx,
    escalated: Boolean(trial.escalated),
    escalationSteps: trial.escalationSteps || 0,
    modelBeforeOnset,
    hintBeforeOnset,
    // "The full model was given" covers both Pip speaking the target and the
    // ladder having reached the full_model rung, which are the same event
    // described from two sides.
    fullModelBeforeOnset: modelBeforeOnset || fullModelRungBeforeOnset,
    openedAtFullModel: trial.nominalRung === FULL_MODEL_RUNG,
  };
}

/* =========================================================================
 * PHASE 1 B - Attempts Vocalization  (5 boxes, capability audio_energy)
 * ========================================================================= */

/**
 * Section 6.1: "Child makes vocal attempt / any sound in response to prompt."
 *
 * THE POINT OF THE WHOLE AUDIO PATH: this must be true for a child who says
 * "buh". Speech recognition returns a word or nothing, so on that trial it
 * returns nothing and fires `no-speech`. If this box were judged from the
 * transcript it would be false, and Phase 1 would be permanently red for
 * exactly the pre-verbal children it exists for.
 *
 * So the primary evidence is the RMS envelope, and the transcript is only a
 * fallback for when audio monitoring was unavailable. A recognised word IS a
 * vocalisation; the reverse does not hold, and that asymmetry is the box.
 *
 * @param {object} trial normalised trial record
 * @param {object} [opts]
 * @param {number} [opts.minDurationMs=150]
 * @returns {{success:boolean, reason:string, evidence:object}}
 */
export function judgePhase1B(trial, opts = {}) {
  const minDurationMs = opts.minDurationMs ?? MIN_VOCALISATION_MS;
  const qualifying = qualifyingVocalisations(trial, { minDurationMs });
  const monitored = Boolean(trial.audio && trial.audio.monitored);

  if (qualifying.length) {
    const first = qualifying[0];
    const wordless = !trial.transcript;
    return {
      applicable: true,
      success: true,
      reason: wordless
        ? `Vocalised for ${Math.round(first.durationMs)}ms in the response window `
          + 'with no word recognised. That is a Phase 1 B success: the child '
          + 'attempted a vocalization.'
        : `Vocalised for ${Math.round(first.durationMs)}ms in the response window.`,
      evidence: {
        source: 'audio_energy',
        vocalisations: qualifying.length,
        firstOnsetMs: first.startedAt - trial.promptEndedAt,
        longestMs: Math.max(...qualifying.map((v) => v.durationMs)),
        peakRms: first.peakRms,
        noiseFloor: trial.audio.noiseFloor,
        wordless,
      },
    };
  }

  // Fallback: the audio path was not running, but recognition heard something.
  // Never the other way round - a transcript cannot disprove a vocalisation.
  if (!monitored && (trial.spoke || trial.transcript)) {
    return {
      applicable: true,
      success: true,
      reason: 'No audio monitoring on this trial, but speech was detected. '
            + 'Counted, with the caveat that a wordless vocalisation on a trial '
            + 'like this one would have been missed entirely.',
      evidence: { source: 'speech_recognition_fallback', transcript: trial.transcript },
    };
  }

  const rejected = ((trial.audio && trial.audio.vocalisations) || []).length;
  return {
    // DELIVERY, not declaration. A game may declare audio_energy and still
    // meet a microphone the parent declined; on that trial nobody was
    // listening, and "the child made no sound" is a claim the record cannot
    // support. It is excluded from the window rather than scored as a failure
    // the child did not earn.
    applicable: monitored,
    success: false,
    reason: monitored
      ? (rejected
          ? `${rejected} sound(s) detected, none sustained for ${minDurationMs}ms inside `
            + 'the response window.'
          : 'No sound above the noise floor in the response window.')
      : 'Audio monitoring was not delivered on this trial and nothing was heard, so '
        + 'whether the child vocalised was not observed. Not assessed.',
    evidence: {
      source: monitored ? 'audio_energy' : 'none',
      monitored,
      detected: rejected,
      minDurationMs,
      noiseFloor: trial.audio ? trial.audio.noiseFloor : null,
    },
  };
}

/* =========================================================================
 * PHASE 2 A/B/C - the fading curve  (18 boxes, capability prompt_sequence)
 *
 * All three describe the same event - the child said the word - seen at three
 * points on the fading curve. Section 5: "They are only independent if the
 * game escalates support within the trial and records which rung was live when
 * the child spoke."
 *
 * The three, in increasing difficulty:
 *
 *   A  Responds to Prompt Level   said it during the prompt sequence, at any rung
 *   B  Retrieves Word             said it without the full model being given
 *   C  Produces Independently     said it at the opening rung, no escalation, no hint
 *
 * ENGINE DECISION - the boundaries. Section 5 gives A as "at the level's
 * nominal rung", B as "after a partial cue but before the full cue escalated",
 * and C as "hintFired === false and no escalation". Read literally, A and C
 * are the same test and B is the loosest, which inverts the difficulty
 * ordering the component NAMES carry ("responds" is easier than "retrieves" is
 * easier than "independently"). The reading below preserves that ordering and
 * makes the three properly nested: C implies B implies A. Nesting matters,
 * because under section 7.5 the level takes its lowest box - so C, the hardest,
 * is what actually gates the level, and A and B show a therapist how far along
 * the fading curve the child is rather than three copies of one number.
 * ========================================================================= */

/**
 * @param {object} trial normalised trial record
 * @param {object} [opts]
 * @param {string} [opts.target] the expected word; defaults to the record's
 *        targetWord, then targetObject
 * @param {number} [opts.phase=2] strictness band to match under
 * @returns {{A:object, B:object, C:object}} each {success, reason, evidence}
 */
export function judgePhase2(trial, opts = {}) {
  const phase = opts.phase ?? 2;
  const target = opts.target ?? trial.targetWord ?? trial.targetObject;
  const shape = trialShape(trial);

  const match = targetProduced({
    phase,
    target,
    transcript: trial.transcript,
    alternatives: trial.alternatives,
  });

  const evidence = {
    rungAtOnset: shape.rungAtOnset,
    nominalRung: shape.nominalRung,
    escalationSteps: shape.escalationSteps,
    hintBeforeOnset: shape.hintBeforeOnset,
    fullModelBeforeOnset: shape.fullModelBeforeOnset,
    onsetLatencyMs: shape.onsetLatencyMs,
    matchedIn: match.matchedIn,
  };

  // ---- A: responded to the prompt at all --------------------------------
  // Requires the utterance to be a response to THIS prompt: after it ended,
  // inside the response window. A child talking over the prompt has not
  // responded to it, however correct the word.
  const aSuccess = match.produced && shape.respondedToPrompt;
  const A = {
    applicable: true,
    success: aSuccess,
    reason: !match.produced
      ? match.reason
      : (shape.respondedToPrompt
          ? `Produced the target ${shape.onsetLatencyMs}ms after the prompt, at the `
            + `"${shape.rungAtOnset}" rung.`
          : (shape.spoke
              ? `Produced the target, but onset was ${shape.onsetLatencyMs}ms from the `
                + 'prompt - outside the response window, so it is not a response to it.'
              : 'The target appears in the transcript but no speech onset was recorded.')),
    evidence,
  };

  // ---- B: retrieved it without being told -------------------------------
  const B = {
    applicable: true,
    success: aSuccess && !shape.fullModelBeforeOnset,
    reason: !aSuccess
      ? 'Did not respond to the prompt with the target word.'
      : (shape.fullModelBeforeOnset
          ? 'The full model was given before the child spoke, so the word was '
            + 'echoed rather than retrieved.'
          : 'Produced the target without the full model.'),
    evidence,
  };

  // ---- C: no help at all -------------------------------------------------
  // "No prompt needed" cannot be true of a trial that OPENED with the full
  // model. Without this clause a level whose nominal rung is full_model would
  // score C true and B false, breaking the nesting.
  const cBlocked = shape.escalated || shape.hintBeforeOnset || shape.openedAtFullModel;
  const C = {
    applicable: true,
    success: aSuccess && !shape.fullModelBeforeOnset && !cBlocked,
    reason: !aSuccess
      ? 'Did not respond to the prompt with the target word.'
      : (shape.openedAtFullModel
          ? 'The trial opened at the full model, so the production cannot be independent.'
          : (shape.escalated
              ? `Support was escalated ${shape.escalationSteps} rung(s) from `
                + `"${shape.nominalRung}" to "${shape.rungAtOnset}" before the child spoke.`
              : (shape.hintBeforeOnset
                  ? 'The time-delay hint fired before the child spoke.'
                  : (shape.fullModelBeforeOnset
                      ? 'The full model was given before the child spoke.'
                      : `Produced the target at the opening rung "${shape.nominalRung}" `
                        + 'with no escalation and no hint.')))),
    evidence,
  };

  return { A, B, C };
}

/* =========================================================================
 * PHASE 6 D - Independence  (5 boxes, capability prompt_sequence)
 * ========================================================================= */

/**
 * Section 5, Phase 6 D: "`modelPlayed === false` for this trial and
 * `hintFired === false`."
 *
 * ENGINE DECISION - two refinements to that literal reading.
 *
 * 1. An utterance is required. Independence is a property of something the
 *    child produced; a silent trial with no model and no hint is not an
 *    independent sentence, it is silence, and scoring it true would make D the
 *    one box a child improves by saying nothing.
 * 2. "For this trial" is read as "before the child spoke", for the reason in
 *    trialShape() - a model that played afterwards did not prompt anything.
 *
 * D is deliberately NOT conditioned on the sentence being correct. Section 5
 * calls that out as what makes the box independent: a wrong sentence produced
 * unaided still demonstrates independence, and Phase 6 A/B/C are where its
 * correctness is judged.
 *
 * @param {object} trial normalised trial record
 * @returns {{D:{success:boolean, reason:string, evidence:object}}}
 */
export function judgePhase6D(trial) {
  const shape = trialShape(trial);
  const spokeInWindow = shape.respondedToPrompt || (shape.spoke && shape.onsetLatencyMs < 0);

  const success = spokeInWindow && !shape.modelBeforeOnset && !shape.hintBeforeOnset;

  return {
    D: {
      applicable: true,
      success,
      reason: !spokeInWindow
        ? 'No utterance in this trial, so there is no independent production to credit.'
        : (shape.modelBeforeOnset
            ? 'Pip modelled the sentence before the child spoke.'
            : (shape.hintBeforeOnset
                ? 'The time-delay hint fired before the child spoke.'
                : 'The child produced a sentence with no model and no hint.')),
      evidence: {
        modelBeforeOnset: shape.modelBeforeOnset,
        hintBeforeOnset: shape.hintBeforeOnset,
        onsetLatencyMs: shape.onsetLatencyMs,
        rungAtOnset: shape.rungAtOnset,
        // Named so nobody later reads a Green D as "said it correctly".
        judgesCorrectness: false,
      },
    },
  };
}

/* =========================================================================
 * APPLICABILITY - which boxes this TRIAL can answer
 *
 * Three ways a box that a game is otherwise entitled to score can fail to be
 * assessable on one particular trial:
 *
 *   1. The framework marks it conditional and the condition is not met.
 *      Phase 3 D: on a trial where the target was never going to be modelled,
 *      "did not echo" is free.
 *   2. Its capability was declared but not DELIVERED - the microphone was
 *      declined, so Phase 1 B was not observed on this trial.
 *   3. It is a box this game does not instrument at all, which is the
 *      per-game case and is already handled by boxState().
 *
 * The engine decides all of it, from the record. A game supplies evidence -
 * `modelScheduled`, whether the child spoke, whether audio was monitored - and
 * never the verdict, for the same reason capabilities are read from the
 * registry rather than the request.
 *
 * A not-applicable outcome is EXCLUDED from the box's ten-play window rather
 * than written as a failure: the window is ten APPLICABLE plays.
 * ========================================================================= */

/** Did the child produce anything at all that this trial could judge? */
function producedSomething(trial) {
  if (!trial) return false;
  if (trial.respondedWithinWindow) return true;
  if (trial.spoke && trial.onsetLatencyMs !== null && trial.onsetLatencyMs < 0) return true;
  return Boolean(trial.transcript);
}

/**
 * Is a conditional box assessable on this trial?
 *
 * @param {string} conditionId a CONDITIONALS key
 * @param {object} trial normalised record
 * @returns {{applicable:boolean, reason:string}}
 */
export function judgeCondition(conditionId, trial) {
  if (conditionId !== 'model_opportunity') {
    return { applicable: true, reason: `Unknown condition "${conditionId}"; not excluded.` };
  }

  const spoke = producedSomething(trial);
  const modelOnOffer = Boolean(trial && (trial.modelScheduled || trial.modelPlayed));

  if (!spoke) {
    return {
      applicable: false,
      reason: 'No utterance on this trial, so there is no production to call an echo or not.',
    };
  }
  if (!modelOnOffer) {
    return {
      applicable: false,
      reason: 'The target was never going to be modelled on this trial, so "did not echo it" '
            + 'is free and would score true while measuring nothing.',
    };
  }
  return {
    applicable: true,
    reason: trial.modelPlayed
      ? 'The target was modelled on this trial and the child spoke.'
      : 'The prompt sequence would have modelled the target; the child answered first.',
  };
}

/**
 * Applicability for every box of a level, on this trial.
 *
 * Covers boxes the game judges as well as boxes the engine judges: Phase 3 D
 * is the game's verdict and the engine's applicability, and that split is the
 * point - the game can see whether the phrase was right, and only the record
 * can see whether the question was answerable.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {object} input.trial NORMALISED record, or null when a game sent none
 * @param {Object<string,boolean>} [input.capabilities]
 * @returns {Object<string, {applicable:boolean, reason:string, why:string}>}
 *          Only boxes that are NOT applicable, or that are conditional and
 *          are. Boxes absent from the map are unconditionally assessable.
 */
export function judgeApplicability({ phase, level, trial, capabilities }) {
  const out = {};
  const caps = validateCapabilities(capabilities).normalised;

  for (const box of getAssessableBoxes(phase, level, capabilities)) {
    // 1. framework-declared conditions
    if (box.conditional) {
      const verdict = judgeCondition(box.conditional, trial);
      out[box.code] = { ...verdict, why: `condition:${box.conditional}` };
      continue;
    }

    // 2. declared but not delivered. Only meaningful where the capability
    //    itself is what does the measuring - see judgePhase1B.
    if (box.requires === 'audio_energy' && caps.audio_energy) {
      const monitored = Boolean(trial && trial.audio && trial.audio.monitored);
      if (!monitored && !producedSomething(trial)) {
        out[box.code] = {
          applicable: false,
          why: 'capability_not_delivered:audio_energy',
          reason: 'This game instruments audio energy, but it was not delivered on this trial '
                + '(no microphone stream), and nothing was heard either. Whether the child '
                + 'vocalised was not observed.',
        };
      }
    }
  }

  return out;
}

/* =========================================================================
 * THE DISPATCHER
 * ========================================================================= */

/** Which component codes this module can judge, per phase. */
export const JUDGED_BY_ENGINE = Object.freeze({
  1: Object.freeze(['B']),
  2: Object.freeze(['A', 'B', 'C']),
  6: Object.freeze(['D']),
});

/**
 * Judge every instrumentation-dependent box of one level from one trial record.
 *
 * Returns ONLY the boxes the engine judges. The rest of the level - the
 * content boxes - remain the game's, and `gameMustJudge` names them so a game
 * can see exactly what it still owes without consulting the framework itself.
 *
 * A box whose capability the game has not declared is not judged, even if the
 * record happens to carry the fields. `not_instrumented` is a statement about
 * the build, and a chart that means one thing when a field happens to be
 * present and another when it does not is worse than one that means nothing.
 *
 * @param {object} input
 * @param {number} input.phase
 * @param {number} input.level
 * @param {object} input.trial            RAW or normalised trial record
 * @param {Object<string,boolean>} [input.capabilities] the game's declaration
 * @param {string} [input.target]         expected word/phrase, for Phase 2
 * @param {object} [input.options]        passed to the individual judges
 * @returns {{ok:boolean, errors:string[], warnings:string[],
 *            trial:object|null,
 *            components:Object<string,{success:boolean, reason:string, evidence:object}>,
 *            judged:Array<{boxId:string, code:string, name:string, success:boolean}>,
 *            gameMustJudge:Array<{boxId:string, code:string, name:string}>,
 *            notInstrumented:Array<{boxId:string, code:string, requires:string}>,
 *            observational:Array<{boxId:string, code:string}>}}
 */
export function judgeTrial({ phase, level, trial, capabilities, target, options = {} }) {
  const empty = {
    ok: false, errors: [], warnings: [], trial: null,
    components: {}, judged: [], gameMustJudge: [], notInstrumented: [], observational: [],
  };

  const row = getBoxesForLevel(phase, level);
  if (row.length === 0) {
    return { ...empty, errors: [`No such level: phase ${phase}, level ${level}.`] };
  }

  const capCheck = validateCapabilities(capabilities);
  if (!capCheck.ok) return { ...empty, errors: capCheck.errors };
  const caps = capCheck.normalised;

  // Only require the record to carry the fields the DECLARED capabilities
  // need, and only those this level actually uses. A Phase 3 game declaring
  // audio_energy is not asked for an audio block on a Phase 3 trial.
  const neededHere = new Set(
    row.filter((b) => b.requires && caps[b.requires]).map((b) => b.requires)
  );

  // Already normalised records pass through validation unchanged; validating
  // twice is cheap and means a caller cannot skip it.
  const v = validateTrialRecord(
    stripDerived(trial),
    { requires: [...neededHere] }
  );
  if (!v.ok) return { ...empty, errors: v.errors, warnings: v.warnings };

  const record = v.normalised;
  const assessable = getAssessableBoxes(phase, level, capabilities);
  const judgeable = new Set(JUDGED_BY_ENGINE[phase] || []);

  let judgedComponents = {};
  if (phase === 1 && caps.audio_energy) {
    judgedComponents.B = judgePhase1B(record, options);
  }
  if (phase === 2 && caps.prompt_sequence) {
    judgedComponents = {
      ...judgedComponents,
      ...judgePhase2(record, { target, phase, ...options }),
    };
  }
  if (phase === 6 && caps.prompt_sequence) {
    judgedComponents = { ...judgedComponents, ...judgePhase6D(record) };
  }

  // Keep only what this level actually has a box for. Phase 2 has A/B/C at
  // every level, but this guards the shape rather than assuming it.
  const components = {};
  const judged = [];
  for (const box of assessable) {
    if (!judgeable.has(box.code)) continue;
    const result = judgedComponents[box.code];
    if (!result) continue;
    components[box.code] = result;
    judged.push({
      boxId: box.boxId, code: box.code, name: box.name,
      success: result.success,
      applicable: result.applicable !== false,
    });
  }

  // Which boxes this TRIAL can answer. Computed for the whole level, not only
  // the boxes the engine judges: Phase 3 D is the game's verdict and the
  // engine's applicability.
  const applicability = judgeApplicability({ phase, level, trial: record, capabilities });

  const gameMustJudge = assessable
    .filter((b) => !(b.code in components))
    .map((b) => ({
      boxId: b.boxId,
      code: b.code,
      name: b.name,
      ...(applicability[b.code] && applicability[b.code].applicable === false
        ? { applicable: false, reason: applicability[b.code].reason }
        : {}),
    }));

  const notInstrumented = row
    .filter((b) => boxState(b, capabilities) === 'not_instrumented')
    .map((b) => ({ boxId: b.boxId, code: b.code, requires: b.requires }));

  const observational = row
    .filter((b) => b.observational)
    .map((b) => ({ boxId: b.boxId, code: b.code }));

  return {
    ok: true,
    errors: [],
    warnings: v.warnings,
    trial: record,
    components,
    judged,
    gameMustJudge,
    notInstrumented,
    observational,
    // Per-box, per-trial. Feed straight into scoring.scoreAttempt().
    applicability,
    delivered: record.delivered,
  };
}

/**
 * Drop the fields validateTrialRecord() derives, so a record can be re-validated.
 *
 * `escalated`, `spoke` and the rest are outputs, not inputs, and the validator
 * rejects unknown keys - which is right, because a typo'd field must not pass
 * silently. This lets an already-normalised record round-trip.
 */
function stripDerived(record) {
  if (typeof record !== 'object' || record === null) return record;
  const {
    escalated, escalationSteps, respondedWithinWindow, spoke, delivered, ...rest
  } = record;
  return rest;
}

/**
 * Merge engine judgements into a game's component object.
 *
 * The engine's verdict wins on every box it judges: a game that also submits
 * one for those boxes is asserting something it cannot see better than the
 * record can. Disagreements are returned rather than swallowed - a game whose
 * own idea of "independent" differs from the engine's is a bug worth finding,
 * and it is invisible if the value is quietly replaced.
 *
 * @param {Object<string, boolean|object>} supplied the game's components
 * @param {Object<string, {success:boolean}>} judgedComponents from judgeTrial()
 * @returns {{components:object, overridden:string[],
 *            disagreements:Array<{code:string, game:boolean, engine:boolean}>}}
 */
export function mergeJudgements(supplied = {}, judgedComponents = {}) {
  const components = { ...supplied };
  const overridden = [];
  const disagreements = [];

  for (const [code, verdict] of Object.entries(judgedComponents)) {
    if (code in supplied) {
      const raw = supplied[code];
      const gameSaid = typeof raw === 'object' && raw !== null ? Boolean(raw.success) : Boolean(raw);
      overridden.push(code);
      if (gameSaid !== verdict.success) {
        disagreements.push({ code, game: gameSaid, engine: verdict.success });
      }
    }
    components[code] = {
      success: verdict.success,
      judgedBy: 'engine',
      reason: verdict.reason,
      evidence: verdict.evidence,
      // Carried for the audit trail. scoring.scoreAttempt reads applicability
      // from judgeApplicability(), never from a component - see its docblock.
      ...(verdict.applicable === false ? { applicable: false } : {}),
    };
  }

  return { components, overridden, disagreements };
}

export default {
  tokenise,
  targetProduced,
  judgeCondition,
  judgeApplicability,
  trialShape,
  judgePhase1B,
  judgePhase2,
  judgePhase6D,
  judgeTrial,
  mergeJudgements,
  JUDGED_BY_ENGINE,
};
